import { LegacySortApiMapping, SortApiMapping } from '@app/enums';
import { AmenityApiMapping } from '@app/enums/amenity-api-mapping.enum';
import { LocationType, LocationTypeEnum } from '@app/enums/location-type.enum';
import { PropertyTypeApiMapping } from '@app/enums/property-type-api-mapping.enum';
import {
    ListingAmenitySlug,
    ListingGeoPrefix,
    ListingPropertyTypeSlug,
    ListingUrlPart,
    ListingUrlPrefix,
} from '@app/models/listing.model';

const polishVoivodeships = [
    'zachodniopomorskie',
    'pomorskie',
    'warminsko-mazurskie',
    'lubuskie',
    'wielkopolskie',
    'kujawsko-pomorskie',
    'mazowieckie',
    'podlaskie',
    'dolnoslaskie',
    'lodzkie',
    'lubelskie',
    'opolskie',
    'slaskie',
    'swietokrzyskie',
    'malopolskie',
    'podkarpackie',
];

export const amenityMap: Record<ListingAmenitySlug, AmenityApiMapping> = {
    [ListingAmenitySlug.Wifi]: AmenityApiMapping.Internet,
    [ListingAmenitySlug.Parking]: AmenityApiMapping.Parking,
    [ListingAmenitySlug.Catering]: AmenityApiMapping.Catering,
    [ListingAmenitySlug.Tv]: AmenityApiMapping.Tv,
    [ListingAmenitySlug.SwimmingPool]: AmenityApiMapping.SwimmingPool,
    [ListingAmenitySlug.Jacuzzi]: AmenityApiMapping.Jacuzzi,
    [ListingAmenitySlug.ChildrensArea]: AmenityApiMapping.Playground,

    // unsupported in NOP, still needed for legacy reasons
    [ListingAmenitySlug.AirConditioning]: AmenityApiMapping.AirConditioning,
    [ListingAmenitySlug.Fireplace]: AmenityApiMapping.Fireplace,
    [ListingAmenitySlug.EVCharger]: AmenityApiMapping.EVCharger,
    [ListingAmenitySlug.WithBreakfast]: AmenityApiMapping.WithBreakfast,
};

export const unsupportedAmenityList = [
    AmenityApiMapping.AirConditioning,
    AmenityApiMapping.Fireplace,
    AmenityApiMapping.EVCharger,
    AmenityApiMapping.WithBreakfast,
];

export const propertyTypeMap: Record<ListingPropertyTypeSlug, PropertyTypeApiMapping> = {
    [ListingPropertyTypeSlug.RuralTourism]: PropertyTypeApiMapping.RuralTourism,
    [ListingPropertyTypeSlug.Apartment]: PropertyTypeApiMapping.Apartment,
    [ListingPropertyTypeSlug.Chalet]: PropertyTypeApiMapping.Chalet,
    [ListingPropertyTypeSlug.HolidayHome]: PropertyTypeApiMapping.HolidayHome,
    [ListingPropertyTypeSlug.Hostel]: PropertyTypeApiMapping.Hostel,
    [ListingPropertyTypeSlug.Hotel]: PropertyTypeApiMapping.Hotel,
    [ListingPropertyTypeSlug.StudentHostel]: PropertyTypeApiMapping.StudentHostel,
    [ListingPropertyTypeSlug.Guesthouse]: PropertyTypeApiMapping.Guesthouse,
    [ListingPropertyTypeSlug.Motel]: PropertyTypeApiMapping.Motel,
    [ListingPropertyTypeSlug.WorkerAccommodation]: PropertyTypeApiMapping.WorkerAccommodation,
    [ListingPropertyTypeSlug.Resort]: PropertyTypeApiMapping.Resort,
    [ListingPropertyTypeSlug.Pension]: PropertyTypeApiMapping.Pension,
    [ListingPropertyTypeSlug.Villa]: PropertyTypeApiMapping.Villa,
    [ListingPropertyTypeSlug.Inn]: PropertyTypeApiMapping.Inn,
};

export const ignoredPropertyTypeList = [PropertyTypeApiMapping.WorkerAccommodation];

export const sortingMap: Record<SortApiMapping, LegacySortApiMapping> = {
    [SortApiMapping.PriceAsc]: LegacySortApiMapping.PriceAsc,
    [SortApiMapping.PriceDesc]: LegacySortApiMapping.PriceDesc,
    [SortApiMapping.RatingDesc]: LegacySortApiMapping.RatingDesc,
    [SortApiMapping.DiscountDesc]: LegacySortApiMapping.DiscountDesc,
    [SortApiMapping.DistanceFromCenterAsc]: LegacySortApiMapping.DistanceFromCenterAsc,
    [SortApiMapping.DistanceFromBeachAsc]: LegacySortApiMapping.DistanceFromBeachAsc,
};

export const getAmenityFromSlug = (amenity: ListingAmenitySlug) =>
    amenity in amenityMap ? amenityMap[amenity] : undefined;

export const getPropertyTypeFromSlug = (propertyType: ListingPropertyTypeSlug) =>
    propertyType in propertyTypeMap ? propertyTypeMap[propertyType] : undefined;

export const isAmenityCheck = (path: string) =>
    Object.values(ListingAmenitySlug).includes(path as ListingAmenitySlug);

// Unsupported in NocApi, but still supported in our ui
export const isIgnoredPropertyTypeCheck = (propertyTypeId: PropertyTypeApiMapping) =>
    ignoredPropertyTypeList.includes(propertyTypeId);

export const isSupportedAmenityCheck = (amenityId: AmenityApiMapping) =>
    !unsupportedAmenityList.includes(amenityId);

export const isPropertyTypeCheck = (path: string) =>
    Object.values(ListingPropertyTypeSlug).includes(path as ListingPropertyTypeSlug);

export const getListingSlugFromUrl = (prefix: string, details?: string[]) => {
    if (isAmenityCheck(prefix)) {
        if (details?.[0] === ListingGeoPrefix.Regiony) {
            return details[1];
        }

        if (details?.[0]?.length) {
            return details[0];
        }

        return undefined;
    }

    if (prefix === ListingGeoPrefix.Regiony) {
        return details?.[0];
    }

    if (prefix === ListingGeoPrefix.Noclegi) {
        if (details?.[0] === ListingUrlPart.Sortuj) {
            return undefined;
        }

        return details?.[0];
    }

    return undefined;
};

export const VALID_NOCLEGI_TYPES: ReadonlySet<string> = new Set([
    LocationTypeEnum.City,
    LocationTypeEnum.Voivodeship,
]);
export const VALID_REGIONY_TYPES: ReadonlySet<string> = new Set([LocationTypeEnum.Region]);
export const VALID_COUNTRY_TYPES: ReadonlySet<string> = new Set([LocationTypeEnum.Country]);

export const VALID_GEO_TYPES: ReadonlySet<string> = new Set([
    ...VALID_NOCLEGI_TYPES,
    ...VALID_REGIONY_TYPES,
    ...VALID_COUNTRY_TYPES,
]);

export const VALID_LISTING_PREFIX = [
    ...Object.values(ListingGeoPrefix),
    ...Object.values(ListingAmenitySlug),
    ...Object.values(ListingPropertyTypeSlug),
];

const dynamicUrlChecks = {
    amenity: isAmenityCheck,
    propertyType: isPropertyTypeCheck,
};

export const isValidListingPrefix = (prefix: string) =>
    VALID_LISTING_PREFIX.includes(prefix as ListingUrlPrefix);

const getSegments = (path: string) => path.split('/').filter(Boolean);

/**
 * SSR listing url check
 * @param path url
 * @param ssrUrlList list of urls, each url can have whitespace at the end (e.g. `/noclegi/*`),
 *                    or can start with dynamic amenity var (`/[amenity]/`)
 * @returns boolean
 */
export const isNewListing = (path: string, ssrUrlList?: string[]) => {
    const prefixMatch = path.match(/^\/([^/]+)\//);

    if (prefixMatch && isValidListingPrefix(prefixMatch[1])) {
        const segments = getSegments(path);

        // Country listings are not supported yet
        const slug = getListingSlugFromUrl(segments[0], segments.slice(1));

        if (slug && polishVoivodeships.includes(slug)) {
            // Voivodeships have same URL structure as cities so we need to exclude them manually
            return false;
        }

        const parsedUrlList = Array.isArray(ssrUrlList) ? ssrUrlList : [];

        return parsedUrlList.some(url => {
            if (path === url) {
                return true;
            }

            const urlSegments = getSegments(url);

            if (urlSegments.length !== segments.length) {
                return false;
            }

            return urlSegments.every((urlSegment, index) => {
                const segment = segments[index];
          
                // Wildcard
                if (urlSegment === '*') {
                    return true;
                }
          
                // Dynamic param handler (`[amenity]` or `[propertyType]`)
                const match = urlSegment.match(/^\[(.+)\]$/);

                if (match) {
                    const paramName = match[1] as keyof typeof dynamicUrlChecks;

                    return dynamicUrlChecks[paramName]?.(segment as any) ?? false;
                }
          
                return urlSegment === segment;
            });
        });
    }

    return false;
};

// get base geo url
export const getBaseGeoUrl = (pathname: string, locationType: LocationType) => {
    const [prefix, ...details] = pathname.split('/').filter(part => part.length);
    const geoSlug = getListingSlugFromUrl(prefix, details);
    let newPathname = pathname;
    const pathnames = {
        [ListingGeoPrefix.Regiony]: `/${ListingGeoPrefix.Regiony}/${geoSlug}/`,
        [ListingGeoPrefix.Noclegi]: `/${ListingGeoPrefix.Noclegi}/${geoSlug}/`,
    };

    if (
        locationType === LocationTypeEnum.Region &&
        pathname !== pathnames[ListingGeoPrefix.Regiony]
    ) {
        newPathname = pathnames[ListingGeoPrefix.Regiony];
    } else if (
        [LocationTypeEnum.City, LocationTypeEnum.Voivodeship].includes(
            locationType as LocationTypeEnum,
        ) &&
        pathname !== pathnames[ListingGeoPrefix.Noclegi]
    ) {
        newPathname = pathnames[ListingGeoPrefix.Noclegi];
    }

    return newPathname;
};
